import { LOCIZE_NS, MUTATION_KEYS } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { type BookAppointmentVariables } from 'pages/AppointmentsReserve/types';
import { type Appointment } from 'types/appointments';
import { useUploadDocument } from './useUploadDocument';

export const useBookAppointment = (): UseMutationResult<
  AxiosResponse<Appointment>,
  Error,
  BookAppointmentVariables
> => {
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { mutateAsync: uploadDocument } = useUploadDocument();
  return useMutation<AxiosResponse<Appointment>, Error, BookAppointmentVariables>({
    mutationKey: [MUTATION_KEYS.BOOK_APPOINTMENT],
    mutationFn: ({ payload }) => AppointmentService.book(payload),
    onSuccess: async ({ data }, { document }) => {
      if (document) {
        await uploadDocument({ appointmentId: data.id, document });
      }
      toast.success(t('AppointmentsReserve.success'), { id: 'book-appointment-success' });
      navigate(ROUTES.APPOINTMENTS_SUCCESS, { state: data });
    },
    onError: () => {
      toast.error(t('AppointmentsReserve.error'), { id: 'book-appointment-error' });
    },
  });
};
